import { useEffect, useRef, useState } from 'react';
import { useApp } from '../app/store.ts';
import { api } from '../data/api.ts';
import { Dictation, dictationAvailable } from '../todos/dictate.ts';
import { noteFromDictation, readCommand } from '../todos/capture.ts';
import { parseReminder } from '../todos/parse.ts';
import { todos } from '../todos/store.ts';
import { Icon } from './Icon.tsx';

/**
 * Schnellaufnahme über der Liste (docs/KONZEPT-ERINNERUNGEN.md, Abschnitt 4.3).
 *
 * Das Mikrofon in der Erinnerungsliste legt Erinnerungen an, das im Editor schreibt
 * Text — beides entscheidet der Ort. Hier steht keiner fest, also entscheidet der
 * gesprochene Befehl: „neue Notiz …" oder „neue Erinnerung …".
 *
 * Ohne Befehl wird gefragt statt geraten. Die Rückfrage kostet einen Tastendruck;
 * ein Zuruf, der still am falschen Ort landet, kostet mehr.
 */

const KEEP_MESSAGE_MS = 12_000;

type Phase =
  | { kind: 'idle' }
  | { kind: 'recording' }
  | { kind: 'working' }
  | { kind: 'asking'; text: string }
  | { kind: 'done'; text: string }
  | { kind: 'problem'; text: string };

export function Capture(): JSX.Element | null {
  const { captureNote } = useApp();
  const [available, setAvailable] = useState(false);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const dictation = useRef(new Dictation());

  useEffect(() => {
    void dictationAvailable().then(setAvailable);
    const recorder = dictation.current;
    return () => recorder.cancel();
  }, []);

  useEffect(() => {
    if (phase.kind !== 'done' && phase.kind !== 'problem') return undefined;
    const timer = setTimeout(() => setPhase({ kind: 'idle' }), KEEP_MESSAGE_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  if (!available) return null;

  async function asNote(text: string): Promise<void> {
    setPhase({ kind: 'working' });
    // Der Server macht daraus eine Überschrift, wenn er kann. Kann er nicht, bildet
    // sie die App selbst — der Weg darf daran nicht hängen.
    const headline = await api
      .title(text)
      .then((result) => result.title)
      .catch(() => null);
    await captureNote(noteFromDictation(text, headline));
    setPhase({ kind: 'done', text: 'Als Notiz angelegt.' });
  }

  async function asReminder(text: string): Promise<void> {
    setPhase({ kind: 'working' });
    const parsed = parseReminder(text);
    await todos.add({ text: parsed.title, dueAt: parsed.dueAt, repeat: parsed.repeat });
    setPhase({ kind: 'done', text: `Erinnerung: ${parsed.title}` });
  }

  async function handle(spoken: string): Promise<void> {
    const command = readCommand(spoken);
    if (command.text === '') {
      setPhase({ kind: 'problem', text: 'Nichts verstanden.' });
      return;
    }
    if (!command.explicit) {
      setPhase({ kind: 'asking', text: command.text });
      return;
    }
    if (command.target === 'note') await asNote(command.text);
    else await asReminder(command.text);
  }

  async function toggle(): Promise<void> {
    if (phase.kind === 'recording') {
      setPhase({ kind: 'working' });
      try {
        const spoken = await dictation.current.stop();
        if (spoken) await handle(spoken);
        else setPhase({ kind: 'problem', text: 'Nichts verstanden.' });
      } catch {
        setPhase({ kind: 'problem', text: 'Erkennung fehlgeschlagen.' });
      }
      return;
    }
    try {
      await dictation.current.start();
      setPhase({ kind: 'recording' });
    } catch {
      setPhase({ kind: 'problem', text: 'Kein Zugriff auf das Mikrofon.' });
    }
  }

  const busy = phase.kind === 'working';

  return (
    <div className="capture">
      <button
        className={`mic mic-head${phase.kind === 'recording' ? ' mic-on' : ''}`}
        onClick={() => void toggle()}
        disabled={busy}
        aria-label={phase.kind === 'recording' ? 'Aufnahme beenden' : 'Diktieren — „neue Notiz …" oder „neue Erinnerung …"'}
        title={phase.kind === 'recording' ? 'Aufnahme beenden' : 'Diktieren — „neue Notiz …" oder „neue Erinnerung …"'}
      >
        <Icon
          name={busy ? 'wait' : phase.kind === 'recording' ? 'stop' : 'mic'}
          filled={phase.kind === 'recording'}
        />
      </button>

      {phase.kind === 'asking' && (
        <div className="capture-note capture-ask">
          <p className="capture-heard">„{phase.text}"</p>
          <div className="inline">
            <button className="ghost" onClick={() => void asReminder(phase.text)}>
              Erinnerung
            </button>
            <button className="ghost" onClick={() => void asNote(phase.text)}>
              Notiz
            </button>
            <button className="capture-undo" onClick={() => setPhase({ kind: 'idle' })}>
              Verwerfen
            </button>
          </div>
        </div>
      )}

      {(phase.kind === 'recording' || phase.kind === 'working' || phase.kind === 'done' || phase.kind === 'problem') && (
        <p className={phase.kind === 'problem' ? 'capture-note capture-problem' : 'capture-note'}>
          {phase.kind === 'recording'
            ? 'Aufnahme läuft — zum Beenden erneut drücken.'
            : phase.kind === 'working'
              ? 'Einen Moment …'
              : phase.text}
        </p>
      )}
    </div>
  );
}
